import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-fc-navy text-white" data-testid="reset-password-page">
      <div className="relative hidden lg:flex flex-col bg-[#060A24] p-12 overflow-hidden">
        <div className="absolute inset-0 fc-grid-pattern opacity-30 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3 z-10">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-10 w-auto" />
          <span className="text-white font-black tracking-[0.18em] text-xs">FREDDY COMPUTE ACADEMY</span>
        </Link>
        <div className="relative z-10 m-auto max-w-md w-full">
          <img src="/brand/freddy-seal.webp" alt="Freddy seal" className="w-28 h-28 object-contain mb-8 opacity-90" />
          <p className="fc-overline mb-4">SET A NEW PASSWORD</p>
          <h1 className="fc-h-section text-4xl xl:text-5xl leading-[1.05] mb-6">
            Lock it down.
          </h1>
          <p className="text-white/65 leading-relaxed text-sm">
            All previous sessions will be invalidated when you set the new password.
          </p>
        </div>
        <p className="relative z-10 text-xs text-white/35 tracking-wider">© 2026 Freddy Compute</p>
      </div>

      <div className="flex flex-col p-6 sm:p-12">
        <div className="m-auto w-full max-w-md">
          <h2 className="fc-h-section text-3xl sm:text-4xl mb-3">Reset password</h2>
          <p className="text-white/60 mb-8 text-sm">Choose a new password for your account.</p>

          {!token ? (
            <div className="bg-fc-red/10 border border-fc-red/40 text-fc-red p-4 text-sm" data-testid="reset-missing-token">
              Missing reset token. <Link to="/forgot-password" className="font-bold underline">Request a new link</Link>.
            </div>
          ) : done ? (
            <div className="bg-[#10B981]/10 border border-[#10B981]/40 p-5 flex items-start gap-3" data-testid="reset-success">
              <CheckCircle2 className="text-[#10B981] flex-shrink-0 mt-1" size={18} />
              <div>
                <p className="font-bold mb-1">Password updated</p>
                <p className="text-white/70 text-sm">Redirecting you to login…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
              {error && (
                <div className="flex items-center gap-2 bg-fc-red/10 border border-fc-red/40 text-fc-red text-sm p-3">
                  <AlertCircle size={16} /> {error}
                </div>
              )}
              <label className="block">
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70 block mb-2">New password</span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-4 py-3 outline-none"
                  data-testid="reset-new-password"
                />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70 block mb-2">Confirm password</span>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={6}
                  className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-4 py-3 outline-none"
                  data-testid="reset-confirm-password"
                />
              </label>
              <button type="submit" disabled={busy} className="btn-fc-red w-full justify-center" data-testid="reset-submit-btn">
                {busy ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
